import type { FC } from 'react';
import { lazy } from 'react';
import { useMemoOne } from 'use-memo-one';
import type {
  ShipperTransactionsParams,
  TransactionType,
} from 'src/apis/capacity-management/types';
import { useCapacityCategories } from 'src/apis/capacity-management/use-capacity-categories';
import { useCapacityManagementRoot } from 'src/apis/capacity-management/use-capacity-management-root';
import { useMarketAreas } from 'src/apis/capacity-management/use-market-areas';
import { useShipperTransactions } from 'src/apis/capacity-management/use-shipper-transactions';
import { useOptionalReshuffleServiceRoot } from 'src/apis/reshuffle-service/use-reshuffle-service';
import { useOptionalSurrenderRoot } from 'src/apis/surrender-service/use-surrender-root';
import { PageHeader } from 'src/components/layout/page-header';
import { PageSpinner } from 'src/components/spinner-container';
import { useShipperTransactionsPageParams } from 'src/pages/reporting/shipper-transactions/use-page-params';

const TransactionsPage = lazy(() => import('./page'));

export const ShipperTransactions: FC = () => {
  const capacityManagementRoot = useCapacityManagementRoot();
  const capacityCategories = useCapacityCategories({ capacityManagementRoot });
  const marketAreas = useMarketAreas({ capacityManagementRoot });
  const reshuffleServiceRoot = useOptionalReshuffleServiceRoot();
  const surrenderRoot = useOptionalSurrenderRoot();

  const pageParams = useShipperTransactionsPageParams();

  const hasReshuffleTransactions =
    Boolean(reshuffleServiceRoot) &&
    Boolean(capacityManagementRoot.response?.data.isCaMaSubscribedTso);

  const params = useMemoOne<ShipperTransactionsParams>(() => {
    const {
      start,
      pageSize,
      networkPointId,
      shipperId,
      transactionTypeSpecificId,
      transactionType,
      ...rest
    } = pageParams.value;

    // merging 'DIRECT_FCFS', 'FCFS' into one filter
    const processedTransactionType: TransactionType[] = transactionType.flatMap(
      (type) =>
        type === 'DIRECT_FCFS' || type === 'FCFS'
          ? ['DIRECT_FCFS', 'FCFS']
          : [type]
    );

    return {
      offset: start,
      limit: pageSize,
      networkPointId: networkPointId.map((id) => id.value),
      shipperId: shipperId.map((id) => id.value),
      transactionTypeSpecificId: transactionTypeSpecificId
        ? [transactionTypeSpecificId]
        : [],
      transactionType: processedTransactionType,
      ...rest,
    };
  }, [pageParams.value]);

  const shipperTransactions = useShipperTransactions({
    capacityManagementRoot,
    params,
  });

  if (!capacityManagementRoot.response || !surrenderRoot.loaded)
    return <PageSpinner />;

  return (
    <div className="grid items-center gap-xl">
      <PageHeader title="Shipper Transactions" />

      <TransactionsPage
        capacityManagementRoot={capacityManagementRoot}
        shipperTransactions={shipperTransactions}
        pageParams={pageParams}
        capacityCategories={capacityCategories}
        marketAreas={marketAreas}
        hasReshuffleTransactions={hasReshuffleTransactions}
        isSurrenderForAllEnabled={
          !!surrenderRoot.response?.data._links.isSurrenderForAllEnabled
        }
      />
    </div>
  );
};
